import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { getAdminUser } from '../firebase/firestore';
import { AdminUser } from '../firebase/types';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: FirebaseUser | null;
  adminUser: AdminUser | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          // adminUsersコレクションからユーザー情報を取得
          const adminUserData = await getAdminUser(firebaseUser.uid);
          if (adminUserData) {
            setAdminUser(adminUserData);
            setIsAuthenticated(true);
          } else {
            // adminUsersに存在しない場合は認証失敗
            setUser(null);
            setAdminUser(null);
            setIsAuthenticated(false);
            await signOut(auth);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching admin user:', error);
          setUser(null);
          setAdminUser(null);
          setIsAuthenticated(false);
        }
      } else {
        setUser(null);
        setAdminUser(null);
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const firebaseUser = userCredential.user;

      // adminUsersコレクションからユーザー情報を取得
      const adminUserData = await getAdminUser(firebaseUser.uid);
      if (adminUserData) {
        setAdminUser(adminUserData);
        setIsAuthenticated(true);
        return true;
      } else {
        // adminUsersに存在しない場合はログアウト
        await signOut(auth);
        // eslint-disable-next-line no-console
        console.error(
          'Admin user not found in adminUsers collection:',
          firebaseUser.uid
        );
        return false;
      }
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Login error:', error);
      // より詳細なエラー情報をログに出力
      if (error.code) {
        // eslint-disable-next-line no-console
        console.error('Error code:', error.code);
        // eslint-disable-next-line no-console
        console.error('Error message:', error.message);
      }
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
      setUser(null);
      setAdminUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Logout error:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        adminUser,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
